import { BoxesIcon, KeyIcon, KeyRoundIcon, MailCheckIcon } from 'lucide-solid';
import {
  EnvironmentPagePath,
  getSlug,
  replacePathParams
} from '~/components/AppRouter/utils.ts';
import { getLabelComponent } from '~/components/Layout/LeftNav/utils.tsx';
import {
  ApiErrorInstance,
  EnvironmentData,
  getConnectionDetailsQuery,
  getConnectionListQuery,
  isApi5xxResponseStatus,
  toggleEnvironmentConnection,
  withHandleError
} from '~/api';
import { HttpStatusCode } from 'axios';
import { getLocalizedString } from '~/i18n/utils.ts';
import { ConnectionData } from '~/api/types/connectionData.ts';
import { ConfirmModalValues } from '~/components/ui/Modal/ConfirmModalContext.tsx';
import { QueryClient } from '@tanstack/solid-query';
import { ToastContextValue } from '~/components/ui/Toast';
import * as zod from 'zod';
import {
  getSnakeCase,
  getTranslationKeyFunction
} from '~/utils/commonUtils.ts';
import { isFeatureEnabled } from '~/utils/features/featureFlag.tsx';
import { FeatureFlagKeys } from '~/api/types/featureFlagData.ts';
import { TimeoutUnit } from '~/api/types/envSessionData.ts';

const TRANSLATION_BASE_PATH = `pages.full_stack_authentication`;

const getTranslationKey = (translationStr: string): string =>
  `${TRANSLATION_BASE_PATH}.${translationStr}`;

const getAuthMethodsTranslationKey = getTranslationKeyFunction(
  `${TRANSLATION_BASE_PATH}.auth_methods`
);

const getSessionsTranslationKey = getTranslationKeyFunction(
  `${TRANSLATION_BASE_PATH}.sessions`
);

const getRedirectsTranslationKey = getTranslationKeyFunction(
  `${TRANSLATION_BASE_PATH}.redirects`
);

const getPasskeysTranslationKey = getTranslationKeyFunction(
  `${TRANSLATION_BASE_PATH}.auth_methods.passkeys`
);

const DEFAULT_IDLE_SESSION_TIMEOUT_IN_MINUTES = 10080;

enum AuthenticationTabTypes {
  USER_MANAGEMENT = 'user-management',
  AUTHENTICATION_METHODS = 'auth-methods',
  SESSIONS = 'sessions',
  REDIRECTS = 'redirects'
}

enum AuthMethodNavType {
  PASSWORDLESS = 'passwordless',
  SOCIAL_CONNECTIONS = 'social',
  ENTERPRISE_SSO = 'sso',
  PASSKEYS = 'passkeys'
}

enum PasswordlessMethodType {
  MAGIC_LINK = 'LINK',
  VERIFICATION_CODE = 'OTP',
  MAGIC_LINK_AND_VERIFICATION_CODE = 'LINK_OTP'
}

enum PasswordlessValidityUnit {
  SECONDS = 'seconds',
  MINUTES = 'minutes'
}

enum SessionTimeoutUnit {
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAYS = 'days'
}

enum SessionSettingsField {
  ABSOLUTE_SESSION_TIMEOUT = 'absoluteSessionTimeout',
  ACCESS_TOKEN_EXPIRY = 'accessTokenExpiry',
  IDLE_SESSION_TIMEOUT = 'idleSessionTimeout'
}

enum VerificationCodeLength {
  FOUR_DIGITS = '4',
  SIX_DIGITS = '6',
  EIGHT_DIGITS = '8'
}

const SESSION_TIMEOUT_UNIT_OPTIONS = [
  SessionTimeoutUnit.MINUTES,
  SessionTimeoutUnit.HOURS,
  SessionTimeoutUnit.DAYS
];

const SESSION_TIMEOUT_UNIT_LIMITS = {
  [SessionTimeoutUnit.MINUTES]: {
    absoluteSessionTimeout: [1, 525600],
    accessTokenExpiry: [1, 43200],
    idleSessionTimeout: [1, 43200]
  },
  [SessionTimeoutUnit.HOURS]: {
    absoluteSessionTimeout: [1, 8760],
    accessTokenExpiry: [1, 720],
    idleSessionTimeout: [1, 720]
  },
  [SessionTimeoutUnit.DAYS]: {
    absoluteSessionTimeout: [1, 365],
    accessTokenExpiry: [1, 30],
    idleSessionTimeout: [1, 30]
  }
};

const getAuthMethodMenuLabelTranslationPath = (translationStr: string) =>
  `pages.full_stack_authentication.auth_methods.${translationStr}`;

const getAuthMethodLeftNavConfig = (selectedEnvironment: EnvironmentData) => {
  const selectedEnvironmentId = selectedEnvironment?.id;
  return [
    {
      items: [
        {
          iconComponent: MailCheckIcon,
          labelComponent: getLabelComponent({
            translationKey: getAuthMethodMenuLabelTranslationPath(
              `passwordless_nav_label`
            ),
            className: 'text-fg-muted'
          }),
          href: getSlug.environmentById(
            selectedEnvironmentId,
            replacePathParams(
              EnvironmentPagePath.ENVIRONMENT_AUTHENTICATION_METHODS,
              {
                tabType: AuthenticationTabTypes.AUTHENTICATION_METHODS,
                authMethod: AuthMethodNavType.PASSWORDLESS
              }
            ) as EnvironmentPagePath,
            true
          ),
          hidden: false,
          dataTestId: 'auth-passwordless'
        },
        {
          iconComponent: KeyRoundIcon,
          labelComponent: getLabelComponent({
            translationKey:
              getAuthMethodMenuLabelTranslationPath(`passkeys_nav_label`),
            className: 'text-fg-muted'
          }),
          href: getSlug.environmentById(
            selectedEnvironmentId,
            replacePathParams(
              EnvironmentPagePath.ENVIRONMENT_AUTHENTICATION_METHODS,
              {
                tabType: AuthenticationTabTypes.AUTHENTICATION_METHODS,
                authMethod: AuthMethodNavType.PASSKEYS
              }
            ) as EnvironmentPagePath,
            true
          ),
          hidden: !isFeatureEnabled(FeatureFlagKeys.PASSKEYS),
          dataTestId: 'auth-passkeys'
        },
        {
          iconComponent: BoxesIcon,
          labelComponent: getLabelComponent({
            translationKey: getAuthMethodMenuLabelTranslationPath(
              `social_connections_nav_label`
            ),
            className: 'text-fg-muted'
          }),
          href: getSlug.environmentById(
            selectedEnvironmentId,
            replacePathParams(
              EnvironmentPagePath.ENVIRONMENT_AUTHENTICATION_METHODS,
              {
                tabType: AuthenticationTabTypes.AUTHENTICATION_METHODS,
                authMethod: AuthMethodNavType.SOCIAL_CONNECTIONS
              }
            ) as EnvironmentPagePath,
            true
          ),
          hidden: false,
          dataTestId: 'auth-social-login'
        },
        {
          iconComponent: KeyIcon,
          labelComponent: getLabelComponent({
            translationKey: getAuthMethodMenuLabelTranslationPath(
              `enterprise_sso_nav_label`
            ),
            className: 'text-fg-muted'
          }),
          href: getSlug.environmentById(
            selectedEnvironmentId,
            replacePathParams(
              EnvironmentPagePath.ENVIRONMENT_AUTHENTICATION_METHODS,
              {
                tabType: AuthenticationTabTypes.AUTHENTICATION_METHODS,
                authMethod: AuthMethodNavType.ENTERPRISE_SSO
              }
            ) as EnvironmentPagePath,
            true
          ),
          hidden: false,
          dataTestId: 'auth-enterprise-sso'
        }
      ]
    }
  ];
};

const handlePasswordlessConnectionStatus = async (
  connection: ConnectionData,
  intl: any,
  confirm: ConfirmModalValues,
  toaster: ToastContextValue,
  queryClient: QueryClient
) => {
  try {
    if (connection.enabled) {
      confirm.show({
        title: getLocalizedString(
          'pages.social_login.disable_connection_confirm_header',
          { intl }
        ),
        description: getLocalizedString(
          'pages.social_login.disable_connection_confirm_message',
          { intl }
        ),
        primaryActionDataTestId: `auth-passwordless-disable`,
        onConfirm: async () => {
          confirm.setIsLoading(true);
          await enableAndDisablePasswordlessConnectionStatus(
            connection,
            intl,
            toaster,
            queryClient
          );
          confirm.setIsLoading(false);
          confirm.close();
        }
      });
      return;
    }
    await enableAndDisablePasswordlessConnectionStatus(
      connection,
      intl,
      toaster,
      queryClient
    );
  } catch (e) {
    console.error(e);
  }
};

const enableAndDisablePasswordlessConnectionStatus = async (
  connection: ConnectionData,
  intl: any,
  toaster: ToastContextValue,
  queryClient: QueryClient
) => {
  try {
    const status: boolean = !!connection.enabled;
    await toggleEnvironmentConnection(connection.id, !status);
    if (status) {
      toaster.toast().success({
        description: getLocalizedString(
          'pages.social_login.connection_disabled_success_toast',
          {
            intl
          }
        )
      });
    } else {
      toaster.toast().success({
        description: getLocalizedString(
          'pages.social_login.connection_enabled_success_toast',
          {
            intl
          }
        )
      });
    }

    // Update connection Details in tanstack cache
    await queryClient.refetchQueries({
      queryKey: getConnectionDetailsQuery(connection.id),
      type: 'all'
    });

    // update connection list in tanstack cache
    await queryClient.refetchQueries({
      queryKey: getConnectionListQuery(),
      exact: true,
      type: 'all'
    });
  } catch (e) {
    toaster.toast().error({
      description: getLocalizedString(
        'pages.social_login.connection_status_update_failed_toast_message',
        {
          intl
        }
      )
    });
  }
};

const handlePasskeysConnectionStatus = async (
  connection: ConnectionData,
  intl: any,
  confirm: ConfirmModalValues,
  toaster: ToastContextValue,
  queryClient: QueryClient
) => {
  try {
    if (connection.enabled) {
      confirm.show({
        title: getLocalizedString(
          getPasskeysTranslationKey('disable_connection_confirm_header'),
          { intl }
        ),
        description: getLocalizedString(
          'pages.social_login.disable_connection_confirm_message',
          { intl }
        ),
        primaryActionDataTestId: `auth-passkeys-disable`,
        onConfirm: async () => {
          confirm.setIsLoading(true);
          await enableAndDisablePasskeysConnection(
            connection,
            intl,
            toaster,
            queryClient
          );
          confirm.setIsLoading(false);
          confirm.close();
        }
      });
      return;
    }
    await enableAndDisablePasskeysConnection(
      connection,
      intl,
      toaster,
      queryClient
    );
  } catch (e) {
    confirm.setIsLoading(false);
    console.error(e);
  }
};

const enableAndDisablePasskeysConnection = async (
  connection: ConnectionData,
  intl: any,
  toaster: ToastContextValue,
  queryClient: QueryClient
) => {
  try {
    const status: boolean = !!connection.enabled;
    await toggleEnvironmentConnection(connection.id, !status);
    if (status) {
      toaster.toast().success({
        description: getLocalizedString(
          getPasskeysTranslationKey('connection_disabled_success_toast'),
          {
            intl
          }
        )
      });
    } else {
      toaster.toast().success({
        description: getLocalizedString(
          getPasskeysTranslationKey('connection_enabled_success_toast'),
          {
            intl
          }
        )
      });
    }

    // Update connection Details in tanstack cache
    await queryClient.refetchQueries({
      queryKey: getConnectionDetailsQuery(connection.id),
      type: 'all'
    });

    // update connection list in tanstack cache
    await queryClient.refetchQueries({
      queryKey: getConnectionListQuery(),
      exact: true,
      type: 'all'
    });
  } catch (e) {
    toaster.toast().error({
      description: getLocalizedString(
        'pages.social_login.connection_status_update_failed_toast_message',
        {
          intl
        }
      )
    });
  }
};

const errorTranslationBasePath = 'server_errors.passwordless_connection';
const sessionUpdateErrorTranslationBasePath = 'server_errors.session_settings';
const passkeysUpdateErrorTranslationBasePath = 'server_errors.passkeys';

const handlePasswordlessConnectionUpdateErrors = withHandleError(
  ({ error, intl }) => {
    const serverError = error as ApiErrorInstance;
    if (
      serverError.httpStatus === HttpStatusCode.BadRequest ||
      isApi5xxResponseStatus(serverError.httpStatus)
    ) {
      return getLocalizedString(
        `${errorTranslationBasePath}.connection_update_failed`,
        {
          intl
        }
      );
    }
  }
);

const handleSessionSettingsUpdateErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    return getLocalizedString(
      `${sessionUpdateErrorTranslationBasePath}.connection_update_failed`,
      {
        intl
      }
    );
  }
});

const handlePasskeysConnectionUpdateErrors = withHandleError(
  ({ error, intl }) => {
    const serverError = error as ApiErrorInstance;
    if (
      serverError.httpStatus === HttpStatusCode.BadRequest ||
      isApi5xxResponseStatus(serverError.httpStatus)
    ) {
      return getLocalizedString(
        `${passkeysUpdateErrorTranslationBasePath}.connection_update_failed`,
        {
          intl
        }
      );
    }
  }
);

const addCustomBaseSchemaValidation = (baseSchema, intl: any) => {
  return baseSchema.superRefine((data, ctx) => {
    let {
      absoluteSessionTimeout,
      absoluteSessionTimeoutUnit,
      idleSessionEnabled,
      idleSessionTimeout,
      idleSessionTimeoutUnit,
      accessTokenExpiry,
      accessTokenExpiryUnit
    } = data;

    validateSessionFieldRange(
      ctx,
      absoluteSessionTimeout,
      absoluteSessionTimeoutUnit,
      SessionSettingsField.ABSOLUTE_SESSION_TIMEOUT,
      intl
    );
    validateSessionFieldRange(
      ctx,
      accessTokenExpiry,
      accessTokenExpiryUnit,
      SessionSettingsField.ACCESS_TOKEN_EXPIRY,
      intl
    );

    if (idleSessionEnabled) {
      validateSessionFieldRange(
        ctx,
        idleSessionTimeout,
        idleSessionTimeoutUnit,
        SessionSettingsField.IDLE_SESSION_TIMEOUT,
        intl
      );
    }

    const absoluteSessionTimeoutInMins = getTimeoutInMinutes(
      absoluteSessionTimeout,
      absoluteSessionTimeoutUnit
    );
    const accessTokenExpiryInMins = getTimeoutInMinutes(
      accessTokenExpiry,
      accessTokenExpiryUnit
    );

    if (accessTokenExpiryInMins >= absoluteSessionTimeoutInMins) {
      addSessionValidationIssue(
        ctx,
        `access_token_absolute_session_error_validation_message`,
        SessionSettingsField.ACCESS_TOKEN_EXPIRY,
        intl
      );
    }

    if (idleSessionEnabled) {
      const idleSessionTimeoutInMins = getTimeoutInMinutes(
        idleSessionTimeout,
        idleSessionTimeoutUnit
      );
      if (accessTokenExpiryInMins >= idleSessionTimeoutInMins) {
        addSessionValidationIssue(
          ctx,
          `idle_session_access_token_error_validation_message`,
          SessionSettingsField.IDLE_SESSION_TIMEOUT,
          intl
        );
        addSessionValidationIssue(
          ctx,
          `access_token_idle_session_error_validation_message`,
          SessionSettingsField.ACCESS_TOKEN_EXPIRY,
          intl
        );
      }
      if (idleSessionTimeoutInMins >= absoluteSessionTimeoutInMins) {
        addSessionValidationIssue(
          ctx,
          `idle_session_absolute_session_error_validation_message`,
          SessionSettingsField.IDLE_SESSION_TIMEOUT,
          intl
        );
      }
    }
  });
};

const getZodSessionValidationSchema = (data, intl: any) => {
  const customNumberFieldValidation = () =>
    zod.preprocess(
      (val) => {
        if (val === '' || val === null || val === undefined) return undefined;
        return Number(val);
      },
      zod.number({
        required_error: getLocalizedString(
          getSessionsTranslationKey('required_field_validation_message'),
          { intl }
        )
      })
    );

  let baseSchema = zod.object({
    absoluteSessionTimeout: customNumberFieldValidation(),
    absoluteSessionTimeoutUnit: zod.string(),
    idleSessionEnabled: zod.boolean(),
    idleSessionTimeoutUnit: zod.string(),
    accessTokenExpiry: customNumberFieldValidation(),
    accessTokenExpiryUnit: zod.string(),
    ...(data.idleSessionEnabled && {
      idleSessionTimeout: customNumberFieldValidation()
    })
  });

  return addCustomBaseSchemaValidation(baseSchema, intl);
};

const getTimeoutInMinutes = (
  timeout: number,
  timeoutUnit: SessionTimeoutUnit
) => {
  if (timeoutUnit === SessionTimeoutUnit.DAYS) {
    return timeout * 24 * 60;
  } else if (timeoutUnit === SessionTimeoutUnit.HOURS) {
    return timeout * 60;
  }
  return timeout;
};

const addSessionValidationIssue = (
  ctx: zod.RefinementCtx,
  translationStr: string,
  field: SessionSettingsField,
  intl: any,
  vars?: {
    min: number;
    max: number;
    unit: SessionTimeoutUnit;
  }
) => {
  ctx.addIssue({
    code: zod.ZodIssueCode.custom,
    message: getLocalizedString(getSessionsTranslationKey(translationStr), {
      intl,
      ...(vars && { ...vars })
    }),
    path: [field]
  });
};

const validateSessionFieldRange = (
  ctx: zod.RefinementCtx,
  value: number,
  unit: SessionTimeoutUnit,
  field: SessionSettingsField,
  intl: any
) => {
  const [min, max] = SESSION_TIMEOUT_UNIT_LIMITS[unit][field];
  if (value < min || value > max) {
    addSessionValidationIssue(
      ctx,
      `${getSnakeCase(field)}_range_validation_error_message`,
      field,
      intl,
      {
        min,
        max,
        unit
      }
    );
  }
};

const toUITimeoutUnit = (u: TimeoutUnit) =>
  u.toLowerCase() as SessionTimeoutUnit;

const toApiTimeoutUnit = (u: SessionTimeoutUnit) =>
  u.toUpperCase() as TimeoutUnit;

const getAppTitle = ({
  intl,
  tabType
}: {
  intl: any;
  tabType: AuthenticationTabTypes;
}) => {
  const titleMap: Record<AuthenticationTabTypes, string> = {
    [AuthenticationTabTypes.USER_MANAGEMENT]: getLocalizedString(
      'page_titles.user_management',
      { intl }
    ),
    [AuthenticationTabTypes.AUTHENTICATION_METHODS]: getLocalizedString(
      'app_title.auth_methods_app_title',
      { intl }
    ),
    [AuthenticationTabTypes.REDIRECTS]: getLocalizedString(
      'app_title.redirect_urls_app_title',
      { intl }
    ),
    [AuthenticationTabTypes.SESSIONS]: getLocalizedString(
      'app_title.session_policy_app_title',
      { intl }
    )
  };

  return (
    (tabType && titleMap?.[tabType]) ||
    getLocalizedString('page_titles.authentication', { intl })
  );
};

export {
  AuthenticationTabTypes,
  AuthMethodNavType,
  PasswordlessMethodType,
  VerificationCodeLength,
  PasswordlessValidityUnit,
  SessionTimeoutUnit,
  SessionSettingsField,
  getTranslationKey,
  getAuthMethodsTranslationKey,
  getSessionsTranslationKey,
  getRedirectsTranslationKey,
  getAuthMethodLeftNavConfig,
  getTimeoutInMinutes,
  handlePasswordlessConnectionStatus,
  handlePasskeysConnectionStatus,
  handlePasswordlessConnectionUpdateErrors,
  handleSessionSettingsUpdateErrors,
  handlePasskeysConnectionUpdateErrors,
  DEFAULT_IDLE_SESSION_TIMEOUT_IN_MINUTES,
  SESSION_TIMEOUT_UNIT_LIMITS,
  SESSION_TIMEOUT_UNIT_OPTIONS,
  addSessionValidationIssue,
  validateSessionFieldRange,
  getZodSessionValidationSchema,
  getPasskeysTranslationKey,
  toUITimeoutUnit,
  toApiTimeoutUnit,
  getAppTitle
};
